/* =============================================================
   THE APP IS BUSY

   One counter, incremented while a blocking request is in flight
   and decremented when it settles. <GlobalLoader> watches it.

   WHAT COUNTS AS BLOCKING, and why the decision lives here rather
   than in each page:

     A request that carries PAGINATION is a list fetching a page.
     The list already says it is loading -- in its own foot, in a
     skeleton row, in a spinner where the next page will appear --
     and that is the right place to say it, because the rest of the
     page is still readable and still usable while it happens.
     Covering the screen to fetch page two of a table is the
     interface taking itself away from the reader.

     Everything else is an ACTION: a save, a delete, a record being
     opened, a filter being applied. Those change what the page
     means, so the page should not be touched while they run.

   A counter rather than a boolean, because two actions can overlap
   and the first to finish must not clear the veil the second is
   still behind.
   ============================================================= */

/** Params that mean "this is a page of a list". */
const PAGINATION_KEYS = ['page', 'currentPage', 'limit', 'pageSize', 'offset', 'skip'];

let silentUntil = 0;

/**
 * Treat every request started in the next `ms` as background.
 *
 * For pollers that reach the network through shared services whose
 * signatures have no room for a per-call flag. Time-boxed rather than a
 * toggle, so a poll that throws before it can clear the flag cannot
 * leave the whole app silent afterwards.
 */
export const silenceFor = (ms = 1500) => {
  silentUntil = Math.max(silentUntil, Date.now() + Math.max(0, ms));
};

/**
 * Whether a request should raise the overlay.
 *
 * Exported so the interceptor and any test agree on one rule, and so
 * the rule can be read in one place rather than inferred from four.
 */
export const isBlockingRequest = (config) => {
  if (!config) return false;

  if (Date.now() < silentUntil) return false;

  // An explicit opt-out. Background polling uses it: the notification
  // bell asking for an unread count every 60 seconds is not an action
  // anybody took, and a veil appearing on its own every minute would be
  // the app seizing up for no reason the reader can see.
  if (config.silent === true) return false;

  const params = config.params || {};
  if (PAGINATION_KEYS.some((key) => params[key] !== undefined && params[key] !== null)) {
    return false;
  }

  // Some callers put the page on the query string themselves rather
  // than in `params`, so the URL is checked too.
  const url = String(config.url || '');
  if (/[?&](page|currentPage|limit|pageSize|offset|skip)=/.test(url)) return false;

  return true;
};

let inFlight = 0;
const listeners = new Set();

const emit = () => {
  for (const listener of listeners) {
    try {
      listener(inFlight > 0);
    } catch (error) {
      console.error('Busy listener failed:', error);
    }
  }
};

export const subscribeToBusy = (listener) => {
  listeners.add(listener);
  listener(inFlight > 0);
  return () => listeners.delete(listener);
};

export const startBusy = () => {
  inFlight += 1;
  if (inFlight === 1) emit();
};

export const endBusy = () => {
  if (inFlight === 0) return;
  inFlight -= 1;
  if (inFlight === 0) emit();
};

/** A hard reset, for a route change that abandons whatever was in flight. */
export const resetBusy = () => {
  if (inFlight === 0) return;
  inFlight = 0;
  emit();
};
